import { useTranslation } from 'react-i18next';
import { usePostsStore } from '@/stores/postsStore';
import { useVariationsFilterStore } from '@/stores/variationsFilterStore';
import { useTimezone } from '@/contexts/TimezoneContext';
import { getLanguageName } from '@/utils/languages';
import type { FilterChip } from '@/components/layout/FilterChipsBar';

/**
 * Single source of truth for the active-filter chips (Creator + Variations).
 * Used by FiltersIndicator / VariationFiltersIndicator and ActiveStatesIndicator.
 */
export interface FilterChipsResult {
  chips: FilterChip[];
  clearAll: () => void;
}

export const useCreatorFilterChips = (): FilterChipsResult => {
  const { t } = useTranslation('common');
  const { formatDateOnly } = useTimezone();
  const { filters, setFilter, clearFilters } = usePostsStore();
  // Publishing status options for display labels (inside component for t() access)
  const PUBLISHING_STATUS_OPTIONS: Record<string, string> = {
    all: t('all_publishing_statuses'),
    unmapped: t('unmapped'),
    mapped: t('mapped'),
    scheduled: t('scheduled'),
    publishing: t('publishing'),
    published: t('published'),
    failed: t('failed'),
  };

  // Sort options for display labels (inside component for t() access)
  const SORT_OPTIONS: Record<string, string> = {
    newest: t('sort_newest'),
    oldest: t('sort_oldest'),
    az: t('sort_az'),
    za: t('sort_za'),
  };

  // Build active filter chips
  const activeFilterChips: FilterChip[] = [];

  if (filters.status && filters.status !== 'ALL') {
    activeFilterChips.push({
      key: 'status',
      label: t('filter_status', { status: filters.status }),
      onRemove: () => setFilter('status', 'ALL'),
    });
  }

  if (filters.platforms && filters.platforms.length > 0) {
    activeFilterChips.push({
      key: 'platforms',
      label: t('filter_platforms', { count: filters.platforms.length, platforms: filters.platforms.join(', ') }),
      onRemove: () => setFilter('platforms', null),
    });
  }

  if (filters.contentStatus) {
    const contentStatusLabels: Record<string, string> = {
      DRAFT: t('draft'),
      READY: t('ready_to_shoot'),
    };
    activeFilterChips.push({
      key: 'contentStatus',
      label: contentStatusLabels[filters.contentStatus] || filters.contentStatus,
      onRemove: () => setFilter('contentStatus', null),
    });
  }

  if (filters.publishingStatus && filters.publishingStatus !== 'all') {
    const publishingLabel = PUBLISHING_STATUS_OPTIONS[filters.publishingStatus] || filters.publishingStatus;
    activeFilterChips.push({
      key: 'publishingStatus',
      label: t('filter_publishing_status', { status: publishingLabel }),
      onRemove: () => setFilter('publishingStatus', ''),
    });
  }

  if (filters.tags && filters.tags.trim() !== '') {
    const tags = filters.tags.split(',').filter(Boolean);
    tags.forEach((tag, index) => {
      activeFilterChips.push({
        key: `tag-${index}`,
        label: t('filter_tag', { tag: tag.trim() }),
        onRemove: () => {
          const remainingTags = tags.filter((_, i) => i !== index);
          setFilter('tags', remainingTags.join(','));
        },
      });
    });
  }

  if (filters.search && filters.search.trim() !== '') {
    activeFilterChips.push({
      key: 'search',
      label: t('filter_search', { query: filters.search }),
      onRemove: () => setFilter('search', ''),
    });
  }

  if (filters.dateFrom) {
    activeFilterChips.push({
      key: 'dateFrom',
      label: t('filter_date_from', { date: formatDateOnly(filters.dateFrom) }),
      onRemove: () => setFilter('dateFrom', null),
    });
  }

  if (filters.dateTo) {
    activeFilterChips.push({
      key: 'dateTo',
      label: t('filter_date_to', { date: formatDateOnly(filters.dateTo) }),
      onRemove: () => setFilter('dateTo', null),
    });
  }

  if (filters.sortBy && filters.sortBy !== 'newest') {
    const sortLabel = SORT_OPTIONS[filters.sortBy] || filters.sortBy;
    activeFilterChips.push({
      key: 'sortBy',
      label: t('filter_sort', { sort: sortLabel }),
      onRemove: () => setFilter('sortBy', 'newest'),
    });
  }

  if (filters.mediaType && filters.mediaType !== 'all') {
    const mediaTypeLabels: Record<string, string> = {
      image: t('with_images'),
      video: t('with_videos'),
      pdf: t('with_pdfs'),
      none: t('no_media'),
    };
    activeFilterChips.push({
      key: 'mediaType',
      label: mediaTypeLabels[filters.mediaType] || filters.mediaType,
      onRemove: () => setFilter('mediaType', null),
    });
  }

  return { chips: activeFilterChips, clearAll: clearFilters };
};

export const useVariationFilterChips = (): FilterChipsResult => {
  const { t } = useTranslation('common');
  const { filters, setFilter, clearFilters } = useVariationsFilterStore();
  const chips: FilterChip[] = [];

  if (filters.search.trim() !== '') {
    chips.push({ key: 'search', label: t('filter_search', { query: filters.search }), onRemove: () => setFilter('search', '') });
  }
  if (filters.type !== 'all') {
    chips.push({
      key: 'type',
      label: t('filter_variation_type', { type: filters.type === 'language' ? t('language') : t('custom') }),
      onRemove: () => setFilter('type', 'all'),
    });
  }
  if (filters.status !== 'all') {
    chips.push({
      key: 'status',
      label: t('filter_status', { status: filters.status === 'DRAFT' ? t('draft') : t('ready') }),
      onRemove: () => setFilter('status', 'all'),
    });
  }
  if (filters.language !== 'all') {
    chips.push({
      key: 'language',
      label: t('filter_language', { language: getLanguageName(filters.language) }),
      onRemove: () => setFilter('language', 'all'),
    });
  }
  if (filters.workflowStatus !== 'all') {
    const workflowLabels: Record<string, string> = {
      PENDING: t('pending'),
      APPROVED: t('approved'),
      CHANGES_REQUESTED: t('changes_requested'),
    };
    chips.push({
      key: 'workflowStatus',
      label: t('filter_workflow_status', { status: workflowLabels[filters.workflowStatus] ?? filters.workflowStatus }),
      onRemove: () => setFilter('workflowStatus', 'all'),
    });
  }
  if (filters.labels !== '') {
    const labels = filters.labels.split(',').filter(Boolean);
    labels.forEach((label, index) => {
      chips.push({
        key: `label-${index}`,
        label: t('filter_tag', { tag: label.trim() }),
        onRemove: () => setFilter('labels', labels.filter((_, i) => i !== index).join(',')),
      });
    });
  }

  return { chips, clearAll: clearFilters };
};

/** Route-aware chips for the global navbar: Variations page shows variation filters, everything else the Creator's. */
export const useActiveFilterChips = (pathname: string): FilterChipsResult => {
  const creator = useCreatorFilterChips();
  const variations = useVariationFilterChips();
  return pathname.startsWith('/variations') ? variations : creator;
};
